import { twJoin } from "tailwind-merge";
import { SocketIn, SocketOut } from "~/js/graph/node-socket-connection";
import { extensionsByGenerics } from "~/js/network/mime-type-handler";
import EventAnalytics from "~/js/utilities/EventAnalytics";
import { getUserStore } from "~/solidJs/entities/HeaderMain/models";
import { isUserFree } from "~/solidJs/shared";
import {
	createNodeErrorPrompt,
	createNodeHelpPrompt
} from "~/solidJs/shared/helpers/aiAssistantPrompts";
import { downloadAsset } from "~/solidJs/shared/helpers/fileHelpers";
import { addLocalNodeByGlobalId, findSocket } from "~/solidJs/shared/helpers/nodeHelpers";
import { setDataAtrOnlyInTests } from "~/solidJs/shared/helpers/testHelpers";
import { openPricingPage } from "~/solidJs/shared/models/pageParams/pricingPageParams";
import { ChatGptService } from "~/solidJs/shared/services/ChatGptService";
import { buttonStyles } from "~/solidJs/shared/ui/atoms/Button/Button";
import { setIsChatGptVisible } from "~/solidJs/widgets/ChatGpt/models/isShowChatGpt";
import { setIsModelsLimitModalVisible } from "~/solidJs/widgets/ModelsLimitModalWidget/ModelsLimitModalWidget";
import { OverlayTooltipMethods } from "~/solidJs/widgets/OverlayTooltip";
import Editor from "../editor";
import { NodeEvents, SocketEvents } from "../types/IEventorListners";
import Eventor from "../utilities/Eventor";
import { $div } from "../utilities/TurboDom";
export class NodeLayout {
	baseAskAiButtonStyles = "py-1 px-1.5 label-10-medium";
	/**
	 * @type {{node: HTMLDivElement}}
	 */
	DOM = {};
	eventor = new Eventor();
	socketInfo = {};
	appendedPremium = false;
	/**
	 * @type {NodeType}
	 */
	node;
	/**
	 * @type {{code: string, message: string} | null}
	 */
	errorState = null;
	/**
	 *
	 * @param {import("../nodes/node-definition").NodeDefinition} definition
	 * @param {boolean} redrawNodeConnections
	 * @param {import("./node-socket-connection").GraphNode} node
	 */
	constructor(definition, redrawNodeConnections, node) {
		this.initDom();
		this.initListeners();
		this.setTitle(definition.name);
		this.node = node;
		this.redrawNodeConnections = redrawNodeConnections;
		this.position = { x: 0, y: 0 };
		this.DOM.node.setAttribute("data-test", `layout${definition.globalId}`);
		if (definition.isNeedToRevertSocketOrder) {
			this.DOM.node.classList.add("revert-socket-order");
		}
		this.setHintDescription(definition.description);
	}

	initDom() {
		this.DOM.node = $div("node");
		this.DOM.node.tabIndex = 0;
		this.DOM.nodeWrapper = $div("node-wrapper", this.DOM.node);

		this.DOM.nodeHeader = $div("header", this.DOM.nodeWrapper);
		this.DOM.nodeHeaderLabelGroup = $div("label-group", this.DOM.nodeHeader);
		this.DOM.nodeHeaderLabelContainer = $div("label-container", this.DOM.nodeHeaderLabelGroup);
		this.DOM.nodExecutorContainer = $div("executor-container", this.DOM.nodeHeaderLabelGroup);
		this.DOM.nodeHeaderLabel = $div("label-text", this.DOM.nodeHeaderLabelContainer);
		this.DOM.nodeHeaderHeaderButtonsContainer = $div(
			"header-buttons",
			this.DOM.nodeHeaderLabelContainer
		);

		// this.DOM.nodeHeaderButtons = $div('header-buttons', this.DOM.nodeHeader)
		this.DOM.aiAssistantBtn = document.createElement("button");
		this.DOM.aiAssistantBtn.className = buttonStyles({
			color: "TertiaryOverlay",
			size: "small",
			class: this.baseAskAiButtonStyles
		});
		this.DOM.aiAssistantBtn.innerHTML = `
  		<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path opacity="0.15" fill-rule="evenodd" clip-rule="evenodd" d="M10.9361 3.7075L12.1514 5.9295C12.2741 6.1535 12.4574 6.33683 12.6814 6.4595L14.9034 7.67483C15.4021 7.9475 15.4021 8.6635 14.9034 8.93616L12.6814 10.1515C12.4574 10.2742 12.2741 10.4575 12.1514 10.6815L10.9361 12.9035C10.6634 13.4022 9.94739 13.4022 9.67472 12.9035L8.45939 10.6815C8.33672 10.4575 8.15339 10.2742 7.92939 10.1515L5.70739 8.93616C5.20872 8.6635 5.20872 7.9475 5.70739 7.67483L7.92939 6.4595C8.15339 6.33683 8.33672 6.1535 8.45939 5.9295L9.67472 3.7075C9.94739 3.20883 10.6634 3.20883 10.9361 3.7075Z" fill="currentColor"/>
    <path fill-rule="evenodd" clip-rule="evenodd" d="M4.54668 1.693L4.98401 2.70566C5.04401 2.845 5.15534 2.95633 5.29468 3.01633L6.30734 3.45366C6.78668 3.66033 6.78668 4.34033 6.30734 4.54766L5.29468 4.98433C5.15534 5.04433 5.04401 5.15566 4.98401 5.295L4.54668 6.30766C4.34001 6.787 3.66001 6.787 3.45268 6.30766L3.01601 5.295C2.95601 5.15566 2.84468 5.04433 2.70534 4.98433L1.69268 4.547C1.21334 4.34033 1.21334 3.66033 1.69268 3.453L2.70534 3.01566C2.84468 2.95566 2.95601 2.84433 3.01601 2.705L3.45334 1.693C3.66001 1.21366 4.34001 1.21366 4.54668 1.693Z" fill="currentColor"/>
    <path fill-rule="evenodd" clip-rule="evenodd" d="M3.07673 10.2695L3.40473 11.0288C3.45007 11.1335 3.5334 11.2168 3.63807 11.2622L4.3974 11.5902C4.75673 11.7455 4.75673 12.2555 4.3974 12.4102L3.63807 12.7382C3.5334 12.7835 3.45007 12.8668 3.40473 12.9715L3.07673 13.7308C2.9214 14.0902 2.4114 14.0902 2.25673 13.7308L1.92873 12.9715C1.8834 12.8668 1.80007 12.7835 1.6954 12.7382L0.936065 12.4102C0.576732 12.2548 0.576732 11.7448 0.936065 11.5902L1.6954 11.2622C1.80007 11.2168 1.8834 11.1335 1.92873 11.0288L2.25673 10.2695C2.41207 9.91017 2.9214 9.91017 3.07673 10.2695Z" fill="currentColor"/>
    </svg>
			Ask AI
		`;
		this.DOM.aiAssistantBtn.addEventListener("click", () => this.handleAiAssistantClick());
		this.DOM.nodeHeaderHeaderButtonsContainer.appendChild(this.DOM.aiAssistantBtn);
		this.DOM.close = $div("close", this.DOM.nodeHeaderHeaderButtonsContainer);
		setDataAtrOnlyInTests(() => {
			this.DOM.close.setAttribute("data-test", "close");
		});
		this.DOM.nodeBody = $div("body", this.DOM.nodeWrapper);

		const nodeResizeObserver = new ResizeObserver(() => {
			this.eventor.invoke("resize");
		});
		nodeResizeObserver.observe(this.DOM.node);
		this.initPremiumIntBanner();
	}
	selectQuickTour() {
		this.DOM.node.classList.add("quickTourSelectedMain");
		this.DOM.node.classList.add("quickTourSelectedNodeLayout");
	}
	initPremiumIntBanner() {
		this.DOM.premiumBannerContainer = document.createElement("div");

		this.DOM.premiumBannerContainer.className =
			"absolute bg-white flex-col outline-red-300 outline-solid outline outline-1 w-72 left-full column ml-10 top-0 flex gap-6 rounded-xl p-5";

		this.DOM.premiumBannerText = document.createElement("div");
		this.DOM.premiumBannerBtn = document.createElement("button");

		this.DOM.premiumBannerText.className = "";
		this.DOM.premiumBannerBtn.className =
			"inline-flex items-center justify-center gap-2 rounded-full border border-transparent bg-gradient-to-r from-violet-500 via-fuchsia-400 to-violet-500 bg-size-200 px-4 py-3 text-base font-semibold text-white transition-all duration-500 hover:bg-pos--200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800";
		this.DOM.premiumBannerBtn.textContent = "Upgrade to higher plan";
		this.DOM.premiumBannerBtn.addEventListener("click", () => this.premiumBtnEventListner());

		this.DOM.premiumBannerContainer.append(this.DOM.premiumBannerText);
		this.DOM.premiumBannerContainer.append(this.DOM.premiumBannerBtn);
	}
	premiumBtnEventListner() {
		EventAnalytics.premiumIntParamButtonClick(this.socketInfo);
		openPricingPage({ source: "highParamValue" });
	}
	/**
	 *
	 * @param {{
	 * 	requestedValue: string;
	 * 	displayName: string;
	 * 	name: string;
	 * }} socketInfo
	 *
	 */
	showPremiumIntBanner(socketInfo) {
		this.socketInfo = socketInfo;
		if (!this.appendedPremium) {
			this.appendedPremium = true;
			this.domElement().append(this.DOM.premiumBannerContainer);
		}

		this.DOM.premiumBannerText.textContent = `Oops, ${socketInfo.displayName} number is too high for your plan 😢`;
	}
	closePremiumIntBanner() {
		if (!this.appendedPremium) return;
		this.appendedPremium = false;
		this.DOM.premiumBannerContainer.remove();
	}
	initListeners() {}

	isFitToBoundary(props) {
		const { left, right, top, bottom } = props;
		const { x, y } = this.getPosition();

		if (x > left && y > top) {
			const width = this.getWidth();
			const height = this.getHeight();
			if (x + width < right && y + height < bottom) return true;
		}
		return false;
	}

	addListener = (eventName, listener) => this.eventor.addListener(eventName, listener);
	removeListener = (eventName, listener) => this.eventor.removeListener(eventName, listener);

	setTitle(title) {
		this.DOM.nodeHeaderLabel.innerHTML = title;
	}

	setHintDescription(description) {
		const cleanup = OverlayTooltipMethods.attachOverlayTooltip(
			this.DOM.nodeHeaderLabel,
			description
		);

		// Cleanup tooltip when node is removed
		this.node.eventor.addListener(NodeEvents.Remove, () => {
			cleanup();
		});
	}

	domElement() {
		return this.DOM.node;
	}

	setPosition(x, y, isNeedToRedrawConnections = false) {
		this.position.x = x;
		this.position.y = y;
		this.eventor.invoke("position", { left: x, top: y });
		this.domElement().style.transform = `translate(${x}px, ${y}px)`;
		if (isNeedToRedrawConnections) {
			this.redrawNodeConnections();
		}
	}

	/**
	 *
	 * @returns {x: number, y: number}
	 */
	getPosition() {
		return {
			x: Number(this.position.x),
			y: Number(this.position.y)
		};
	}

	setWidth(px) {
		this.width = px;
		this.domElement().style.width = `${px}px`;
	}

	getNodeWidth() {
		return this.width;
	}

	getWidth() {
		let out = this.width;
		if (this.node.isStyleOpen) {
			out += 600;
		}
		return out;
	}

	getHeight() {
		return this.domElement().offsetHeight;
	}

	appendContent(...domElements) {
		for (const domElement of domElements) {
			this.DOM.nodeBody.append(domElement);
		}
	}

	appendLoader(...domElements) {
		for (const domElement of domElements) {
			this.DOM.nodeWrapper.append(domElement);
		}
	}

	setSelectionStyle(isSelected) {
		this.domElement().classList[isSelected ? "add" : "remove"]("selected");
	}

	lock() {
		this.domElement().classList.add("locked");
	}

	toggleDisability(isDisabled) {
		this.domElement().classList.toggle("disabled", isDisabled);
	}

	unlock() {
		this.domElement().classList.remove("locked");
	}

	/**
	 * Handle AI assistant button click
	 */
	handleAiAssistantClick() {
		const nodeName = this.node.name || "Unknown node";
		let prompt;

		// Analytics
		EventAnalytics.askAi(this.node.globalId, this.errorState?.message);

		if (this.errorState) {
			// Create error prompt
			prompt = createNodeErrorPrompt(nodeName, this.errorState.code, this.errorState.message);
		} else {
			// Create help prompt
			prompt = createNodeHelpPrompt(nodeName);
		}

		// Send message to ChatGPT assistant
		const assistantOptions = ChatGptService.createAssistantOptions({
			inputValue: prompt
		});

		ChatGptService.sendChatMessage(assistantOptions, "node-ai-assistant");

		// Open ChatGPT widget in assistant mode
		setIsChatGptVisible({ assistaint: true, dialogue: false });

		// Clear error state after sending
		this.askedError();
	}

	/**
	 * Set error state and update button style
	 * @param {string} errorCode - Error code
	 * @param {string} errorMessage - Error message
	 */
	setErrorState(errorCode, errorMessage) {
		this.errorState = {
			code: errorCode || "UNKNOWN",
			message: errorMessage || "Unknown error"
		};

		// Update button style to danger
		this.DOM.aiAssistantBtn.className = buttonStyles({
			color: "DangerOverlay",
			size: "small",
			class: this.baseAskAiButtonStyles
		});
		this.DOM.aiAssistantBtn.innerHTML = `
  		<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path opacity="0.15" fill-rule="evenodd" clip-rule="evenodd" d="M10.9361 3.7075L12.1514 5.9295C12.2741 6.1535 12.4574 6.33683 12.6814 6.4595L14.9034 7.67483C15.4021 7.9475 15.4021 8.6635 14.9034 8.93616L12.6814 10.1515C12.4574 10.2742 12.2741 10.4575 12.1514 10.6815L10.9361 12.9035C10.6634 13.4022 9.94739 13.4022 9.67472 12.9035L8.45939 10.6815C8.33672 10.4575 8.15339 10.2742 7.92939 10.1515L5.70739 8.93616C5.20872 8.6635 5.20872 7.9475 5.70739 7.67483L7.92939 6.4595C8.15339 6.33683 8.33672 6.1535 8.45939 5.9295L9.67472 3.7075C9.94739 3.20883 10.6634 3.20883 10.9361 3.7075Z" fill="currentColor"/>
    <path fill-rule="evenodd" clip-rule="evenodd" d="M4.54668 1.693L4.98401 2.70566C5.04401 2.845 5.15534 2.95633 5.29468 3.01633L6.30734 3.45366C6.78668 3.66033 6.78668 4.34033 6.30734 4.54766L5.29468 4.98433C5.15534 5.04433 5.04401 5.15566 4.98401 5.295L4.54668 6.30766C4.34001 6.787 3.66001 6.787 3.45268 6.30766L3.01601 5.295C2.95601 5.15566 2.84468 5.04433 2.70534 4.98433L1.69268 4.547C1.21334 4.34033 1.21334 3.66033 1.69268 3.453L2.70534 3.01566C2.84468 2.95566 2.95601 2.84433 3.01601 2.705L3.45334 1.693C3.66001 1.21366 4.34001 1.21366 4.54668 1.693Z" fill="currentColor"/>
    <path fill-rule="evenodd" clip-rule="evenodd" d="M3.07673 10.2695L3.40473 11.0288C3.45007 11.1335 3.5334 11.2168 3.63807 11.2622L4.3974 11.5902C4.75673 11.7455 4.75673 12.2555 4.3974 12.4102L3.63807 12.7382C3.5334 12.7835 3.45007 12.8668 3.40473 12.9715L3.07673 13.7308C2.9214 14.0902 2.4114 14.0902 2.25673 13.7308L1.92873 12.9715C1.8834 12.8668 1.80007 12.7835 1.6954 12.7382L0.936065 12.4102C0.576732 12.2548 0.576732 11.7448 0.936065 11.5902L1.6954 11.2622C1.80007 11.2168 1.8834 11.1335 1.92873 11.0288L2.25673 10.2695C2.41207 9.91017 2.9214 9.91017 3.07673 10.2695Z" fill="currentColor"/>
    </svg>
			Ask AI
		`;
	}

	/**
	 * Clear error state and restore normal button style
	 */
	askedError() {
		this.errorState = null;

		// Restore normal button style
		this.DOM.aiAssistantBtn.className = buttonStyles({
			color: "TertiaryOverlay",
			size: "small",
			disabled: true,
			class: this.baseAskAiButtonStyles
		});
		this.DOM.aiAssistantBtn.innerHTML = `
  		<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path opacity="0.15" fill-rule="evenodd" clip-rule="evenodd" d="M10.9361 3.7075L12.1514 5.9295C12.2741 6.1535 12.4574 6.33683 12.6814 6.4595L14.9034 7.67483C15.4021 7.9475 15.4021 8.6635 14.9034 8.93616L12.6814 10.1515C12.4574 10.2742 12.2741 10.4575 12.1514 10.6815L10.9361 12.9035C10.6634 13.4022 9.94739 13.4022 9.67472 12.9035L8.45939 10.6815C8.33672 10.4575 8.15339 10.2742 7.92939 10.1515L5.70739 8.93616C5.20872 8.6635 5.20872 7.9475 5.70739 7.67483L7.92939 6.4595C8.15339 6.33683 8.33672 6.1535 8.45939 5.9295L9.67472 3.7075C9.94739 3.20883 10.6634 3.20883 10.9361 3.7075Z" fill="currentColor"/>
    <path fill-rule="evenodd" clip-rule="evenodd" d="M4.54668 1.693L4.98401 2.70566C5.04401 2.845 5.15534 2.95633 5.29468 3.01633L6.30734 3.45366C6.78668 3.66033 6.78668 4.34033 6.30734 4.54766L5.29468 4.98433C5.15534 5.04433 5.04401 5.15566 4.98401 5.295L4.54668 6.30766C4.34001 6.787 3.66001 6.787 3.45268 6.30766L3.01601 5.295C2.95601 5.15566 2.84468 5.04433 2.70534 4.98433L1.69268 4.547C1.21334 4.34033 1.21334 3.66033 1.69268 3.453L2.70534 3.01566C2.84468 2.95566 2.95601 2.84433 3.01601 2.705L3.45334 1.693C3.66001 1.21366 4.34001 1.21366 4.54668 1.693Z" fill="currentColor"/>
    <path fill-rule="evenodd" clip-rule="evenodd" d="M3.07673 10.2695L3.40473 11.0288C3.45007 11.1335 3.5334 11.2168 3.63807 11.2622L4.3974 11.5902C4.75673 11.7455 4.75673 12.2555 4.3974 12.4102L3.63807 12.7382C3.5334 12.7835 3.45007 12.8668 3.40473 12.9715L3.07673 13.7308C2.9214 14.0902 2.4114 14.0902 2.25673 13.7308L1.92873 12.9715C1.8834 12.8668 1.80007 12.7835 1.6954 12.7382L0.936065 12.4102C0.576732 12.2548 0.576732 11.7448 0.936065 11.5902L1.6954 11.2622C1.80007 11.2168 1.8834 11.1335 1.92873 11.0288L2.25673 10.2695C2.41207 9.91017 2.9214 9.91017 3.07673 10.2695Z" fill="currentColor"/>
    </svg>
			Asked
		`;
	}

	/**
	 * Clear error state and restore normal button style
	 */
	clearErrorState() {
		this.errorState = null;

		this.DOM.aiAssistantBtn.className = buttonStyles({
			color: "TertiaryOverlay",
			size: "small",
			class: this.baseAskAiButtonStyles
		});
		this.DOM.aiAssistantBtn.innerHTML = `
  		<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path opacity="0.15" fill-rule="evenodd" clip-rule="evenodd" d="M10.9361 3.7075L12.1514 5.9295C12.2741 6.1535 12.4574 6.33683 12.6814 6.4595L14.9034 7.67483C15.4021 7.9475 15.4021 8.6635 14.9034 8.93616L12.6814 10.1515C12.4574 10.2742 12.2741 10.4575 12.1514 10.6815L10.9361 12.9035C10.6634 13.4022 9.94739 13.4022 9.67472 12.9035L8.45939 10.6815C8.33672 10.4575 8.15339 10.2742 7.92939 10.1515L5.70739 8.93616C5.20872 8.6635 5.20872 7.9475 5.70739 7.67483L7.92939 6.4595C8.15339 6.33683 8.33672 6.1535 8.45939 5.9295L9.67472 3.7075C9.94739 3.20883 10.6634 3.20883 10.9361 3.7075Z" fill="currentColor"/>
    <path fill-rule="evenodd" clip-rule="evenodd" d="M4.54668 1.693L4.98401 2.70566C5.04401 2.845 5.15534 2.95633 5.29468 3.01633L6.30734 3.45366C6.78668 3.66033 6.78668 4.34033 6.30734 4.54766L5.29468 4.98433C5.15534 5.04433 5.04401 5.15566 4.98401 5.295L4.54668 6.30766C4.34001 6.787 3.66001 6.787 3.45268 6.30766L3.01601 5.295C2.95601 5.15566 2.84468 5.04433 2.70534 4.98433L1.69268 4.547C1.21334 4.34033 1.21334 3.66033 1.69268 3.453L2.70534 3.01566C2.84468 2.95566 2.95601 2.84433 3.01601 2.705L3.45334 1.693C3.66001 1.21366 4.34001 1.21366 4.54668 1.693Z" fill="currentColor"/>
    <path fill-rule="evenodd" clip-rule="evenodd" d="M3.07673 10.2695L3.40473 11.0288C3.45007 11.1335 3.5334 11.2168 3.63807 11.2622L4.3974 11.5902C4.75673 11.7455 4.75673 12.2555 4.3974 12.4102L3.63807 12.7382C3.5334 12.7835 3.45007 12.8668 3.40473 12.9715L3.07673 13.7308C2.9214 14.0902 2.4114 14.0902 2.25673 13.7308L1.92873 12.9715C1.8834 12.8668 1.80007 12.7835 1.6954 12.7382L0.936065 12.4102C0.576732 12.2548 0.576732 11.7448 0.936065 11.5902L1.6954 11.2622C1.80007 11.2168 1.8834 11.1335 1.92873 11.0288L2.25673 10.2695C2.41207 9.91017 2.9214 9.91017 3.07673 10.2695Z" fill="currentColor"/>
    </svg>
			Ask AI
		`;
	}

	delete() {
		this.domElement().remove();
	}
}

export class SettingsGroupLayout {
	constructor(title) {
		this.initDom(title);
		this.setIsCollapsed(true);
	}

	initDom(title) {
		this.DOM = {};
		this.DOM.mainContainer = document.createElement("div");
		this.DOM.mainContainer.classList.add("settings-group");
		this.DOM.toggler = document.createElement("button");
		this.DOM.toggler.classList.add("header");
		this.DOM.toggler.textContent = title;

		setDataAtrOnlyInTests(() => {
			this.DOM.toggler.setAttribute("data-test-group", "settings");
		});
		this.DOM.toggler.addEventListener("click", () => {
			this.toggleCollapsed();
		});

		this.DOM.mainContainer.append(this.DOM.toggler);

		this.DOM.body = document.createElement("div");
		this.DOM.body.classList.toggle("collapsed", this.isCollapsed);
		this.DOM.body.classList.add("body");
		this.DOM.mainContainer.append(this.DOM.body);
	}

	setIsCollapsed(isCollapsed) {
		this.isCollapsed = isCollapsed;
		this.DOM.mainContainer.classList.toggle("collapsed", this.isCollapsed);
		if (isCollapsed) {
			this.DOM.body.remove();
		} else {
			this.DOM.mainContainer.append(this.DOM.body);
		}
	}

	toggleCollapsed() {
		this.setIsCollapsed(!this.isCollapsed);
	}

	getDomBodyContainer() {
		return this.DOM.body;
	}

	getDomElement() {
		return this.DOM.mainContainer;
	}
}
/**
 * @param {string}    title     ...
 * @param {SocketDefinition[]}   socketsDefinitions   ...
 * @param {boolean}   isOutput   ...
 */
export class SocketToggleGroupLayout {
	socketsOn = [];
	socketsOff = [];
	DOM = {};
	constructor(group, node) {
		this.name = group.name;
		this.displayName = group.displayName;
		this.socketsOn = group.socketsOn;
		this.socketsOff = group.socketsOff;
		this.node = node;
		this.initDom();
		this.initListeners();
	}
	iterateOverSockets(toggled, socketsNames) {
		for (const socketName of socketsNames) {
			const socket = findSocket(socketName, this.node);
			if (!socket) continue;
			const socketLayout = socket.layout?.domElement();
			socket.setEnabled(toggled, "group", false);
			if (toggled) {
				this.getDomElement().append(socketLayout);
				continue;
			}
			socketLayout.remove();
		}
	}
	toggleSockets(isToggled) {
		if (isToggled) {
			this.iterateOverSockets(false, this.socketsOff);
			this.iterateOverSockets(true, this.socketsOn);
			return;
		}
		this.iterateOverSockets(true, this.socketsOff);
		this.iterateOverSockets(false, this.socketsOn);
	}
	initDom() {
		const id = `${this.name}group`;
		this.DOM.domElement = document.createElement("div");
		this.DOM.label = document.createElement("label");
		this.DOM.label.textContent = this.displayName;
		this.DOM.label.for = id;
		this.DOM.input = document.createElement("input");
		this.DOM.input.checked = true;
		this.DOM.input.id = id;
		this.DOM.input.type = "checkbox";
		this.DOM.inputContainer = document.createElement("div");
		this.DOM.inputContainer.className = "flex gap-2 items-center justify-center";
		this.DOM.domElement.className = "flex gap-4 items-start justify-start flex-col";
		this.DOM.inputContainer.append(this.DOM.label, this.DOM.input);
		this.DOM.domElement.append(this.DOM.inputContainer);
	}
	initListeners() {
		this.DOM.input.addEventListener("change", (e) => {
			this.toggleSockets(!!e.target.checked);
		});
	}
	getDomElement() {
		return this.DOM.domElement;
	}
}
export class GroupLayout {
	constructor(title, name, multiSocket, sockets, isAlwaysExpanded, isNeedToAddHeader = true) {
		this.isExpaned = true;
		this.initDom(title, name, multiSocket);
		this.sockets = sockets;
		this.isAlwaysExpanned = isAlwaysExpanded;

		if (title && isNeedToAddHeader) {
			this.initHeaderDom(title, name);
		}
	}

	initDom(title, name, multiSocket) {
		this.DOM = {};
		this.DOM.group = document.createElement("div");
		this.DOM.group.classList.add("group");
		this.DOM.body = document.createElement("div");
		this.DOM.body.classList.add("body");

		// TODO: refactor
		// We shouldn't use hardcoded name like this
		// to bind html generate logic

		if (title === "Outputs") {
			this.DOM.body.viewers = document.createElement("div");
			this.DOM.body.viewers.classList.add("viewers");
			this.DOM.body.sockets = document.createElement("div");
			this.DOM.body.sockets.classList.add("sockets");
			this.DOM.body.sockets.classList.add("outputs");
			this.DOM.body.sockets.classList.add(name?.replace(/[^a-z0-9]/gi, ""));
			this.DOM.body.classList.add(name?.replace(/[^a-z0-9]/gi, ""));

			this.DOM.body.appendChild(this.DOM.body.viewers);
			if (multiSocket) {
				this.DOM.body.multiSockets = document.createElement("div");
				this.DOM.body.multiSockets.classList.add("sockets");
				this.DOM.body.multiSockets.classList.add("outputs");
				this.DOM.body.multiSockets.classList.add("MultiSocket");
				this.DOM.body.appendChild(this.DOM.body.multiSockets);
			}

			this.DOM.body.appendChild(this.DOM.body.sockets);
		} else if (title !== "Outputs" && title !== "Inputs") {
			this.DOM.group.classList.add("parameters");
		}

		this.DOM.group.appendChild(this.DOM.body);
	}

	initHeaderDom(title, name) {
		this.DOM.header = document.createElement("div");
		this.DOM.header.classList.add("header");
		this.DOM.group.prepend(this.DOM.header);

		this.DOM.label = document.createElement("div");
		this.DOM.label.classList.add("label-text");
		this.DOM.label.textContent = title;

		this.DOM.disclosureIcon = document.createElement("div");
		this.DOM.disclosureIcon.classList.add("disclosure-icon");

		this.DOM.header.append(this.DOM.disclosureIcon, this.DOM.label);
		!this.isExpaned && this.DOM.group.classList.add("collapsed");
		setDataAtrOnlyInTests(() => {
			this.DOM.header.setAttribute("data-test-group", name);
		});
		this.DOM.header.addEventListener("click", () => {
			if (this.isExpaned) {
				this.collapseGroup();
				for (const socket of this.sockets) {
					socket.optionalInfo.isExpaned = false;
				}
			} else {
				this.expandGroup();
				this.DOM.group.classList.remove("collapsed");
				for (const socket of this.sockets) {
					socket.optionalInfo.isExpaned = true;
				}
			}
		});
		// this.collapseGroup()
	}

	domElement() {
		return this.DOM.group;
	}

	recalculateCollapsed() {
		if (this.isAlwaysExpanned) {
		} else {
			this.isExpaned = this.sockets?.every(
				(socket) => socket.optionalInfo.isExpaned === true
			);

			if (this.isExpaned) {
				this.expandGroup();
			} else {
				this.collapseGroup();
			}
		}
	}

	appendContent(...domElements) {
		this.DOM.body.append(...domElements);
		// this.expandGroup()
	}

	appendViewer(...domElements) {
		if (this.DOM.body.viewers) this.DOM.body.viewers.append(...domElements);
		else this.appendContent(...domElements);
	}

	appendSocket(...domElements) {
		if (this.DOM.body.sockets) this.DOM.body.sockets.append(...domElements);
		else this.appendContent(...domElements);
	}

	toggleNewSocketLayout(force) {
		this.DOM.body.sockets.classList.toggle("new-layout", force);
	}

	appendContentAsSingleItem(...domElements) {
		const item = document.createElement("div");
		item.classList.add("item");
		item.append(...domElements);

		this.DOM.body.append(item);
		// this.expandGroup()
	}

	expandGroup() {
		this.isExpaned = true;
		this.domElement().classList.remove("collapsed");
		this.domElement().append(this.DOM.body);
	}

	collapseGroup() {
		this.isExpaned = false;
		this.domElement().classList.add("collapsed");
		this.DOM.body.remove();
	}

	delete() {
		this.domElement().remove();
	}

	replaceMultiSockets(domElements) {
		if (!this.DOM.body.multiSockets) {
			return;
		}
		this.DOM.body.multiSockets.innerHTML = "";
		this.DOM.body.multiSockets.append(...domElements);
	}

	deleteViewers() {
		if (this.DOM.body.viewers) this.DOM.body.viewers.replaceChildren();
	}

	lock() {
		this.domElement().classList.add("disabled");
	}

	unlock() {
		this.domElement().classList.remove("disabled");
	}

	setVisibility(isVisible) {
		if (isVisible) this.domElement().classList.remove("hidden");
		else this.domElement().classList.add("hidden");
	}
}

export class SocketLayout {
	isNeedToUpdatePos = true;

	/**
	 * @type {null| {x: number,  y: number}}
	 */
	prevNodePos = null;

	/**
	 * @type {null| {x: number,  y: number}}
	 */
	connectorPos = null;

	isConnected = false;
	DOM = {};
	isHandleHidden = false;

	/**
	 *
	 * @param definition {SocketDefinition}
	 * @param socket {SocketIn}
	 */
	constructor(definition, socket) {
		this.socket = socket;
		this.isHandleHidden = definition.optionalInfo?.isHandleHidden || false;
		this.initDom(definition);
		if (!definition.optionalInfo.isNeedToHideName) {
			this.setTitle(definition.getName());
			this.setHintDescription(definition.getDescription());
		}
		if (definition.optionalInfo.accessDisabled) {
			this.domElement().classList.add("accessDisabled");
			this.domElement().addEventListener("click", () => {
				EventAnalytics.premiumParamClick(definition.name);
				openPricingPage({ source: "disabledSocket" });
			});
		}
		setDataAtrOnlyInTests(() => {
			this.DOM.socket.setAttribute("data-test", definition.name);
		});
		// TODO : Refactor incoming object type
		// Can we change input object structure to fit it in present
		// FE architecture? Because image/audio/text is not equal type for array
		// Maybe we can historyStore "array/string" param as option, but in dataType historyStore
		// that dataType like image/audio/text
		this.multiImport = definition.dataType === "array";
		if (definition.dataType === "array") {
			this.setDataType(definition.optionalInfo.valueOptions.itemType.dataType);
			this.dataType = definition.optionalInfo.valueOptions.itemType.dataType;
		} else {
			this.setDataType(definition.dataType);
			this.dataType = definition.dataType;
		}

		this.socket = socket;
		const acceptedTypes = ["image", "audio", "video"];
		if (socket instanceof SocketIn && acceptedTypes.includes(this.dataType)) {
			this.initClickImportFunctionality(definition);
			this.DOM.socket.classList.add("in");
		}
	}
	/**
	 *
	 * @param {boolean} bool
	 */
	setIsNeedToUpdatePos(bool) {
		this.isNeedToUpdatePos = bool;
	}
	/**
	 *
	 * @param {boolean} isShown
	 */
	toggleDisabledModelsText(isShown) {
		if (isShown === this.isDisabledModelShown) return;
		this.isDisabledModelShown = isShown;
		if (isShown) {
			this.DOM.label.append(this.DOM.disabledModelText);
			return;
		}
		this.DOM.disabledModelText.remove();
	}
	setIsShown(isShown) {
		this.domElement().classList.toggle("hidden", !isShown);
	}

	setEnabled(isEnabled) {
		this.domElement().classList.toggle("disabled", !isEnabled);
	}

	initClickImportFunctionality(_definition) {
		if (!this.DOM.label) return;
		const input = document.createElement("input");
		input.type = "file";
		input.multiple = this.multiImport;

		const accepted = extensionsByGenerics[this.socket.getDataType()];

		input.accept = accepted ? accepted.join(", ") : "*";
		this.DOM.label.addEventListener("click", () => {
			input.click();
		});
		input.oninput = (e) => {
			if (e.target.files.length > 0) {
				const fileArr = Array.from(e.target.files);

				const filteredFiles = accepted
					? fileArr.filter((file) => {
							const extension = `.${file.name.split(".").pop().toLowerCase()}`;
							return accepted.some((ext) => ext.toLowerCase() === extension);
						})
					: fileArr;
				if (!filteredFiles.length) return;
				addLocalNodeByGlobalId({
					globalId: "Import Files",
					newOutputsFiles: filteredFiles,
					connections: filteredFiles.map((file, _index) => {
						return {
							fromNodeId: this.socket.node.uuid,
							fromNodeSocketName: this.socket.name,
							toNodeSocketName: file.name.split(".")[0]
						};
					}),
					position: {
						x:
							this.socket.node.layout.getPosition().x -
							this.socket.node.layout.getWidth() -
							50,
						y: this.socket.node.layout.position.y
					}
				});
			}
			e.target.value = null;
		};
	}

	initDom(definition) {
		this.DOM.socket = document.createElement("div");
		this.DOM.disabledModelText = document.createElement("div");
		this.DOM.disabledModelText.textContent = "Your limit is overflowed";
		this.DOM.disabledModelText.className = "text-red-500";
		this.DOM.disabledModelText.addEventListener("click", () =>
			setIsModelsLimitModalVisible(true)
		);
		this.DOM.socket.classList.add("socket");
		this.DOM.socket.classList.add(this.socket.getDataType());

		this.DOM.handleConatainer = $div("handle-container", this.DOM.socket);
		this.DOM.connectionSnapper = $div("connection-snapper hidden", this.DOM.socket);
		this.DOM.handle = $div("handle", this.DOM.handleConatainer);

		// Hide handle container if specified
		if (this.isHandleHidden) {
			this.DOM.handleConatainer.classList.add("hidden");
		}
		const shouldHideInTextInputs =
			definition.dataType === "text" && !(this.socket instanceof SocketOut);
		if (!definition.optionalInfo.isNeedToHideName && !shouldHideInTextInputs) {
			this.DOM.label = $div("label-text", this.DOM.socket);

			// Add click handler for output socket file download
			if (this.socket instanceof SocketOut && this.socket.node.globalId !== "Import Files") {
				const fileDataTypes = [
					"image",
					"video",
					"audio",
					"binary",
					"object3d",
					"image_svg"
				];
				const isFileType = fileDataTypes.includes(this.socket.getDataType());

				if (isFileType) {
					this.DOM.label.style.cursor = "pointer";
					this.DOM.label.addEventListener("click", async (e) => {
						if (isUserFree(getUserStore())) return;
						e.stopPropagation();
						const fileValue = await this.socket.tryGetValue();
						if (!fileValue) return;

						// Handle array of files
						if (Array.isArray(fileValue)) {
							for (const item of fileValue) {
								if (item instanceof File) {
									downloadAsset(item);
								}
							}
						} else if (fileValue instanceof File) {
							downloadAsset(fileValue);
						}
					});
				}
			}
		}

		this.DOM.dataEntryContainer = $div("data-entry-container", this.DOM.socket);

		if (definition?.dataType) this.DOM.handle.classList.add(this.socket.getDataType());

		this.DOM.circle = document.createElement("div");
		this.DOM.circle.classList.add("circle");

		this.DOM.handle.append(this.DOM.circle);

		// this.DOM.loading = document.createElement('div')
		// this.DOM.loading.classList.add('loading-socket-icon')
		// this.DOM.socket.prepend(this.DOM.loading)

		this.DOM.handle.addEventListener("mousedown", (e) => e.stopPropagation());

		if (this.socket && this.socket instanceof SocketOut) {
			this.DOM.socket.addEventListener("mouseenter", () => {
				if (this.socket.isBeingDragged) return; // Don't highlight if currently being dragged

				this.socket.node.outputSocketGroup
					.getViewportBySocketName(this.socket.name)
					?.highlight();
			});

			this.DOM.socket.addEventListener("mouseleave", () => {
				if (this.socket.isBeingDragged) return;

				this.socket.node.outputSocketGroup
					.getViewportBySocketName(this.socket.name)
					?.unhighlight();
			});
		}
	}

	toggleConnectionSnapper(force) {
		this.DOM.connectionSnapper.classList.toggle("hidden", force);
	}

	addRemoveBtn() {
		this.DOM.removeBtn = document.createElement("button");
		this.DOM.removeBtn.classList.add("remove-btn");

		this.DOM.removeBtn.addEventListener("click", (e) => {
			e.stopPropagation();
			this.socket?.node?.deleteFile?.(this.socket.value);
			this.delete();
			this.socket.delete();
			this.socket.group.deleteSocketByName(this.socket.name);
			this.socket.group.recalculateNewSockets();
		});
		this.DOM.socket.prepend(this.DOM.removeBtn);
	}

	setTitle(label) {
		this.label = label;
		if (!this.DOM.label) {
			return;
		}
		this.DOM.label.textContent = label;
	}

	setHintDescription(description) {
		if (!this.DOM.label) {
			return;
		}
		const cleanup = OverlayTooltipMethods.attachOverlayTooltip(this.DOM.label, description);

		// Cleanup tooltip when node is removed
		this.socket.node.eventor.addListener(NodeEvents.Remove, () => {
			cleanup();
		});
	}

	setDataType(dataType) {
		if (dataType === "trigger") {
			this.DOM.socket.classList.add("trigger");
			this.DOM.handle.style.color = getDataTypeColor(dataType);
			return;
		}
		this.DOM.circle.style.backgroundColor = getDataTypeColor(dataType);
	}

	markAsOutput() {
		this.domElement().classList.add("output");
		this.domElement().classList.add(this.dataType);
	}

	showLoadingIcon(isShown = false) {
		if (isShown) {
			if (!this.DOM?.loading) {
				this.DOM.loading = document.createElement("div");
				this.DOM.loading.classList.add("loading-socket-icon");
				this.DOM.socket.prepend(this.DOM.loading);
			} else {
				this.DOM.loading.classList.remove("hide");
			}
		} else {
			if (this.DOM?.loading) {
				this.DOM.loading.classList.add("hide");
			}
		}
	}

	domElement = () => this.DOM.socket;
	getConnector = () => this.DOM.handle;

	addSnapEventListeners(callbackEnter, callbackExit) {
		if (this.isHandleHidden) return; // Don't add listeners if handle is hidden
		this.getConnector().addEventListener("mouseenter", callbackEnter);
		this.getConnector().addEventListener("mouseleave", callbackExit);
		this.DOM.connectionSnapper.addEventListener("mouseenter", callbackEnter);
		this.DOM.connectionSnapper.addEventListener("mouseleave", callbackExit);
	}

	removeSnapEventListeners(callbackEnter, callbackExit) {
		if (this.isHandleHidden) return; // Don't remove listeners if handle is hidden (they weren't added)
		this.getConnector().removeEventListener("mouseenter", callbackEnter);
		this.getConnector().removeEventListener("mouseleave", callbackExit);
		this.DOM.connectionSnapper.removeEventListener("mouseenter", callbackEnter);
		this.DOM.connectionSnapper.removeEventListener("mouseleave", callbackExit);
	}

	setDataEntry = (dataEntryElement, isFixedSeed, elementName) => {
		this.DOM.socket.classList.add(elementName);

		this.DOM.dataEntryContainer.innerHTML = "";
		this.DOM.dataEntryContainer.append(dataEntryElement);
		if (isFixedSeed) {
			this.DOM.dataEntryContainer.prepend(isFixedSeed);
		}

		// Add click listener to emit open-select event for enum types
		if (this.socket.dataType === "enum") {
			const socketClickHandler = (event) => {
				if (event.target.closest(".enum-select-button")) {
					return;
				}

				this.socket.eventor.invoke(SocketEvents.OpenSelect, event);
				// Don't trigger if socke
			};

			// Add the click listener to the entire socket
			this.DOM.socket.addEventListener("click", socketClickHandler);

			// Store the handler for potential cleanup
			this.enumSocketClickHandler = socketClickHandler;
		}
	};

	/**
	 * @description returns connector pos from cached delta or getBoundingClientRect
	 * @param {{x: number, y: number}} nodePos
	 * @param {boolean} isNeedToUpdatePos
	 * @returns
	 */
	getConnectorPos(nodePos, isNeedToUpdatePos) {
		if (
			this.prevNodePos &&
			this.connectorPos &&
			!this.isNeedToUpdatePos &&
			!isNeedToUpdatePos
		) {
			return this.getConnectorPosFromNodePosDelta(
				nodePos,
				this.prevNodePos,
				this.connectorPos
			);
		}

		return this.updateConnectorPos(nodePos);
	}

	/**
	 * @description if the node location has changed, we need to update the connector pos with getBoundingClientRect for the actual delta
	 * @param {{x: number, y: number}} nodePos
	 * @returns
	 */
	updateConnectorPos(nodePos) {
		const rect = this.getConnector().getBoundingClientRect();
		const pos = Editor.context.graph.navigation.clientToGraphSpace(
			rect.left + rect.width / 2,
			rect.top + rect.height / 2
		);
		this.mutAssignPos(nodePos, "prevNodePos");
		this.mutAssignPos(pos, "connectorPos");
		this.isNeedToUpdatePos = false;
		return this.connectorPos;
	}

	/**
	 *
	 * @param {{x: number, y: number}} nodePos
	 * @param {{x: number, y: number}} nodePrevPos
	 * @param {{x: number, y: number}} connectorPos
	 */
	getConnectorPosFromNodePosDelta(nodePos, nodePrevPos, connectorPos) {
		const xDelata = nodePos.x - nodePrevPos.x;
		const yDelata = nodePos.y - nodePrevPos.y;

		this.mutAssignPos(nodePos, "prevNodePos");

		this.connectorPos.x = connectorPos.x + xDelata;
		this.connectorPos.y = connectorPos.y + yDelata;

		return this.connectorPos;
	}
	/**
	 * @description asignpos by mutating properties
	 * @param {{x: number, y: number}} pos
	 * @param {keyof SocketLayout} posKey
	 * @param {*} pos
	 */
	mutAssignPos(pos, posKey) {
		if (!this[posKey]) {
			this[posKey] = {
				x: pos.x,
				y: pos.y
			};
			return;
		}
		this[posKey].x = pos.x;
		this[posKey].y = pos.y;
	}

	setConnectable(isConnectable) {
		if (!this.isConnected) this.domElement().classList.add(isConnectable ? "able" : "not-able");
		this.toggleConnectionSnapper(!isConnectable);
	}

	clearAbilityToConnect() {
		this.domElement().classList.remove("able");
		this.domElement().classList.remove("not-able");
		this.toggleConnectionSnapper(true);
	}

	setConnected(isConnected) {
		this.isConnected = isConnected;
		this.domElement().classList.toggle("connected", isConnected);
		this.DOM.dataEntryContainer.classList.toggle(twJoin("opacity-40"), isConnected);
	}

	delete() {
		// Clean up enum socket click handler if it exists
		if (this.enumSocketClickHandler) {
			this.DOM.socket.removeEventListener("click", this.enumSocketClickHandler);
			this.enumSocketClickHandler = null;
		}
		this.domElement().remove();
	}
}

export function getDataTypeColor(dataType) {
	let hex = "#1E3050";
	const colors = {
		object3d: "#7246f6",
		object3d_obj: "#7246f6",
		video: "#FF5C70",
		audio: "#49e78f",
		image: "#f58e62",
		image_svg: "#32a852",
		text: "#5cbef6",
		binary: "#c100ff",
		bool: "#898c93",
		trigger: "#2cd25a"
	};
	if (dataType in colors) {
		hex = colors[dataType];
	}
	return hex;
}
